import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  deleteDoc,
  updateDoc,
  setDoc,
  Timestamp, 
  query, 
  orderBy,
  where,
  getDoc,
  DocumentData
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { NewsItem, NewsFormData, Event, EventFormData, Partner, PartnerFormData, Volunteer, VolunteerApplicationData } from '../types';
import { compressImage } from './imageCompression';

const toNewsItem = (id: string, data: DocumentData): NewsItem => ({
  id,
  title: data.title,
  content: data.content,
  image: data.image || null,
  date: data.date.toDate().toISOString(),
});

export const getNewsItem = async (id: string): Promise<NewsItem | null> => {
  const snapshot = await getDoc(doc(db, 'news', id));
  return snapshot.exists() ? toNewsItem(snapshot.id, snapshot.data()) : null;
};

export const getNews = async (): Promise<NewsItem[]> => {
  try {
    const newsQuery = query(collection(db, 'news'), orderBy('date', 'desc'));
    const querySnapshot = await getDocs(newsQuery);
    
    return querySnapshot.docs.map(newsDoc => toNewsItem(newsDoc.id, newsDoc.data()));
  } catch (error) {
    console.error('Error getting news: ', error);
    throw error;
  }
};

// Uploads a news image under a unique name so two news never share (and overwrite) a file.
const uploadNewsImage = async (image: File): Promise<string> => {
  const compressedImage = await compressImage(image);
  const storageRef = ref(storage, `images/${Date.now()}-${compressedImage.name}`);
  await uploadBytes(storageRef, compressedImage, {
    contentType: compressedImage.type,
    cacheControl: 'public, max-age=31536000',
  });
  return getDownloadURL(storageRef);
};

// A past date backdates the news (noon avoids timezone shifts); otherwise it is dated now
const publicationTimestamp = (publishedOn?: string): Timestamp => {
  const today = new Date().toLocaleDateString('en-CA');
  return publishedOn && publishedOn < today
    ? Timestamp.fromDate(new Date(`${publishedOn}T12:00:00`))
    : Timestamp.now();
};

// News are kept as an archive: the home page shows the 3 latest, /actualites lists them all.
export const addNews = async (newsData: NewsFormData): Promise<NewsItem> => {
  try {
    const imageUrl = newsData.image ? await uploadNewsImage(newsData.image) : null;
    const date = publicationTimestamp(newsData.publishedOn);

    const docRef = await addDoc(collection(db, 'news'), {
      title: newsData.title,
      content: newsData.content,
      image: imageUrl,
      date
    });
    
    return {
      id: docRef.id,
      title: newsData.title,
      content: newsData.content,
      image: imageUrl,
      date: date.toDate().toISOString()
    };
  } catch (error) {
    console.error('Error adding news: ', error);
    throw error;
  }
};

// Updates a news item. A new image replaces the old one; the date only changes if another day is picked.
export const updateNews = async (current: NewsItem, newsData: NewsFormData): Promise<NewsItem> => {
  const image = newsData.image ? await uploadNewsImage(newsData.image) : current.image;
  const currentDay = new Date(current.date).toLocaleDateString('en-CA');
  const date = newsData.publishedOn && newsData.publishedOn !== currentDay
    ? publicationTimestamp(newsData.publishedOn)
    : Timestamp.fromDate(new Date(current.date));

  await updateDoc(doc(db, 'news', current.id), {
    title: newsData.title,
    content: newsData.content,
    image,
    date,
  });

  if (newsData.image && current.image) {
    try {
      await deleteObject(ref(storage, current.image));
    } catch (error) {
      console.error("Erreur lors de la suppression de l'ancienne image:", error);
    }
  }

  return { ...current, title: newsData.title, content: newsData.content, image, date: date.toDate().toISOString() };
};

// Events functions with date validation
export const getEvents = async (): Promise<Event[]> => {
  try {
    // Get today's date at midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Query only future events (or events happening today)
    const eventsQuery = query(
      collection(db, 'events'), 
      where('date', '>=', today.toISOString().split('T')[0]),
      orderBy('date', 'asc')
    );
    
    const querySnapshot = await getDocs(eventsQuery);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title,
        date: data.date,
        time: data.time,
        location: data.location,
        description: data.description || ''
      };
    });
  } catch (error) {
    console.error('Error getting events: ', error);
    throw error;
  }
};

export const addEvent = async (eventData: EventFormData): Promise<Event> => {
  try {
    // Validate that event date is not in the past
    const eventDate = new Date(eventData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight
    
    if (eventDate < today) {
      throw new Error("La date de l'événement doit être aujourd'hui ou dans le futur.");
    }
    
    const docRef = await addDoc(collection(db, 'events'), {
      title: eventData.title,
      date: eventData.date,
      time: eventData.time,
      location: eventData.location,
      description: eventData.description || '',
      createdAt: Timestamp.now()
    });
    
    return {
      id: docRef.id,
      title: eventData.title,
      date: eventData.date,
      time: eventData.time,
      location: eventData.location,
      description: eventData.description || ''
    };
  } catch (error) {
    console.error('Error adding event: ', error);
    throw error;
  }
};

export const updateEvent = async (id: string, eventData: EventFormData): Promise<Event> => {
  try {
    // Validate that event date is not in the past
    const eventDate = new Date(eventData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight
    
    if (eventDate < today) {
      throw new Error("La date de l'événement doit être aujourd'hui ou dans le futur.");
    }
    
    const eventRef = doc(db, 'events', id);
    
    await updateDoc(eventRef, {
      title: eventData.title,
      date: eventData.date,
      time: eventData.time,
      location: eventData.location,
      description: eventData.description || '',
      updatedAt: Timestamp.now()
    });
    
    return {
      id,
      title: eventData.title,
      date: eventData.date,
      time: eventData.time,
      location: eventData.location,
      description: eventData.description || ''
    };
  } catch (error) {
    console.error('Error updating event: ', error);
    throw error;
  }
};

export const deleteNews = async (id: string): Promise<void> => {
  try {
    // Retrieve the news document to get the image URL
    const newsDoc = await getDoc(doc(db, 'news', id));
    const newsData = newsDoc.data();
    
    // Delete the news document
    await deleteDoc(doc(db, 'news', id));
    
    // If the news had an image, delete it from storage
    if (newsData && newsData.image) {
      try {
        // Extract the image path from the URL
        const imageUrl = newsData.image;
        const storageRef = ref(storage, imageUrl);
        await deleteObject(storageRef);
        console.log("Image supprimée:", imageUrl);
      } catch (error) {
        console.error("Erreur lors de la suppression de l'image:", error);
      }
    }
  } catch (error) {
    console.error("Erreur lors de la suppression de l'actualité:", error);
    throw error;
  }
};

export const deleteEvent = async (id: string): Promise<void> => {
  try {
    const eventRef = doc(db, 'events', id);
    await deleteDoc(eventRef);
  } catch (error) {
    console.error('Error deleting event: ', error);
    throw error;
  }
};

// Partners
const uploadPartnerLogo = async (file: File): Promise<string> => {
  // Keep SVG as-is; raster logos are compressed but keep their format (PNG transparency).
  const upload = file.type === 'image/svg+xml' ? file : await compressImage(file);
  const storageRef = ref(storage, `partners/${Date.now()}-${upload.name}`);
  await uploadBytes(storageRef, upload, {
    contentType: upload.type,
    cacheControl: 'public, max-age=31536000',
  });
  return getDownloadURL(storageRef);
};

const deleteStoredLogo = async (logoUrl: string | null | undefined) => {
  // Bundled logos live under /images and are not in Storage
  if (!logoUrl || !logoUrl.startsWith('http')) return;
  try {
    await deleteObject(ref(storage, logoUrl));
  } catch (error) {
    console.error('Error deleting partner logo:', error);
  }
};

export const getPartners = async (): Promise<Partner[]> => {
  try {
    const partnersSnapshot = await getDocs(query(collection(db, 'partners'), orderBy('order', 'asc')));
    return partnersSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        logo: data.logo || null,
        url: data.url || '',
        category: data.category || 'commerce',
        order: typeof data.order === 'number' ? data.order : 0,
      };
    });
  } catch (error) {
    console.error('Error getting partners: ', error);
    throw error;
  }
};

export const addPartner = async (partnerData: PartnerFormData): Promise<Partner> => {
  try {
    const logo = partnerData.logo ? await uploadPartnerLogo(partnerData.logo) : partnerData.logoPath || null;
    const payload = {
      name: partnerData.name,
      url: partnerData.url || '',
      category: partnerData.category,
      order: partnerData.order,
      logo,
      createdAt: Timestamp.now()
    };
    const docRef = await addDoc(collection(db, 'partners'), payload);
    return { id: docRef.id, name: payload.name, url: payload.url, category: payload.category, order: payload.order, logo };
  } catch (error) {
    console.error('Error adding partner: ', error);
    throw error;
  }
};

export const updatePartner = async (partner: Partner, partnerData: PartnerFormData): Promise<Partner> => {
  try {
    let logo = partner.logo;
    if (partnerData.logo) {
      logo = await uploadPartnerLogo(partnerData.logo);
      await deleteStoredLogo(partner.logo);
    }
    const payload = {
      name: partnerData.name,
      url: partnerData.url || '',
      category: partnerData.category,
      order: partnerData.order,
      logo,
      updatedAt: Timestamp.now()
    };
    await updateDoc(doc(db, 'partners', partner.id), payload);
    return { id: partner.id, name: payload.name, url: payload.url, category: payload.category, order: payload.order, logo };
  } catch (error) {
    console.error('Error updating partner: ', error);
    throw error;
  }
};

export const deletePartner = async (partner: Partner): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'partners', partner.id));
    await deleteStoredLogo(partner.logo);
  } catch (error) {
    console.error('Error deleting partner: ', error);
    throw error;
  }
};

export const getVolunteers = async (): Promise<Volunteer[]> => {
  try {
    const volunteersSnapshot = await getDocs(
      query(collection(db, 'volunteers'), orderBy('createdAt', 'desc'))
    );
    
    return volunteersSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message || '',
        age: typeof data.age === 'number' ? data.age : undefined,
        city: data.city || '',
        availability: Array.isArray(data.availability) ? data.availability : [],
        skills: Array.isArray(data.skills) ? data.skills : [],
        motivation: data.motivation || '',
        status: data.status || 'new',
        distribution: data.distribution || '',
        createdAt: data.createdAt
      };
    }) as Volunteer[];
  } catch (error) {
    console.error('Error getting volunteers:', error);
    // Add more specific error handling
    if (error instanceof Error && error.toString().includes('Missing or insufficient permissions')) {
      throw new Error('Erreur d\'accès: Assurez-vous d\'être correctement connecté avec un compte autorisé.');
    }
    throw error;
  }
};

export const addVolunteerApplication = async (applicationData: VolunteerApplicationData): Promise<void> => {
  try {
    const normalizedEmail = applicationData.email.trim().toLowerCase();
    const submissionRef = doc(db, 'volunteers_submissions', normalizedEmail);

    await addDoc(collection(db, 'volunteers'), {
      name: applicationData.name.trim(),
      email: normalizedEmail,
      phone: applicationData.phone.trim(),
      age: applicationData.age,
      city: applicationData.city.trim(),
      availability: applicationData.availability,
      skills: applicationData.skills,
      motivation: applicationData.motivation.trim(),
      message: applicationData.motivation.trim(),
      distribution: applicationData.distribution || '',
      status: 'new',
      createdAt: Timestamp.now()
    });

    await setDoc(submissionRef, {
      timestamp: Timestamp.now(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000))
    });
  } catch (error) {
    console.error('Error adding volunteer application:', error);
    if (error instanceof Error && error.message.toLowerCase().includes('missing or insufficient permissions')) {
      throw new Error('Envoi impossible pour le moment. Vérifiez les règles Firestore (collection volunteers / volunteers_submissions).');
    }
    throw error;
  }
};

export const deleteVolunteer = async (id: string): Promise<void> => {
  try {
    // Get the volunteer data before deletion to handle cleanup
    const volunteerRef = doc(db, 'volunteers', id);
    const volunteerDoc = await getDoc(volunteerRef);
    const volunteerData = volunteerDoc.data();
    
    // Delete the volunteer document
    await deleteDoc(volunteerRef);
    
    // If possible, clean up the corresponding entry in volunteers_submissions
    if (volunteerData && volunteerData.email) {
      try {
        const submissionRef = doc(db, 'volunteers_submissions', volunteerData.email);
        const submissionDoc = await getDoc(submissionRef);
        
        // Only delete if the document exists
        if (submissionDoc.exists()) {
          await deleteDoc(submissionRef);
        }
      } catch (cleanupError) {
        // Log but don't throw, since the main deletion succeeded
        console.error('Error cleaning up volunteer submission record:', cleanupError);
      }
    }
    
    console.log('Volunteer deleted successfully');
  } catch (error) {
    console.error('Error deleting volunteer:', error);
    throw error;
  }
};

